import { useEffect, useState } from 'react';
import {
  Entity,
  RELATION_DEPENDENCY_OF,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import {
  CatalogApi,
  catalogApiRef,
  getEntityRelations,
} from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';

interface UpstreamChainEdge {
  /** ref of the entity that depends on `to` (e.g. a CNAME depending on an A record). */
  from: string;
  to: string;
}

export interface UpstreamLoadBalancerChain {
  proxies: Entity[];
  forwardingRules: Entity[];
  /** A/AAAA records whose IP matches a forwarding rule above. */
  dnsRecords: Entity[];
  /** CNAME records that resolve to one of the dnsRecords above. */
  cnames: Entity[];
  edges: UpstreamChainEdge[];
  loading: boolean;
  error?: string;
}

const EMPTY: UpstreamLoadBalancerChain = {
  proxies: [],
  forwardingRules: [],
  dnsRecords: [],
  cnames: [],
  edges: [],
  loading: true,
};

function isAssetType(entity: Entity, ...types: string[]): boolean {
  const assetType = entity.metadata.annotations?.['gcp.com/asset-type'];
  return !!assetType && types.includes(assetType);
}

function isDnsRecordType(entity: Entity, ...types: string[]): boolean {
  const recordType = entity.metadata.annotations?.['gcp.com/dns-record-type'];
  return !!recordType && types.includes(recordType);
}

/**
 * Fetches every entity that directly depends on any of `parents` (one level
 * of the `dependencyOf` relation), along with the edge back to whichever
 * parent it came from. Relations are read directly off each parent entity
 * (already populated by the catalog), so this needs no relation-query API —
 * just a batched fetch of the target entities.
 */
async function fetchDependents(
  catalogApi: CatalogApi,
  parents: Entity[],
): Promise<{ entities: Entity[]; edges: UpstreamChainEdge[] }> {
  const parentRefsByChild = new Map<string, Set<string>>();
  for (const parent of parents) {
    const parentRef = stringifyEntityRef(parent);
    for (const child of getEntityRelations(parent, RELATION_DEPENDENCY_OF)) {
      const childRef = stringifyEntityRef(child);
      const set = parentRefsByChild.get(childRef) ?? new Set<string>();
      set.add(parentRef);
      parentRefsByChild.set(childRef, set);
    }
  }

  const childRefs = [...parentRefsByChild.keys()];
  if (childRefs.length === 0) return { entities: [], edges: [] };

  const { items } = await catalogApi.getEntitiesByRefs({
    entityRefs: childRefs,
  });
  const entities = items.filter((e): e is Entity => !!e);
  const edges = entities.flatMap(entity => {
    const childRef = stringifyEntityRef(entity);
    return [...(parentRefsByChild.get(childRef) ?? [])].map(parentRef => ({
      from: childRef,
      to: parentRef,
    }));
  });
  return { entities, edges };
}

/**
 * Walks upstream from a UrlMap entity through the real GCP load balancer
 * chain — proxy(s) that route to it, forwarding rule(s) that target those
 * proxies, DNS A/AAAA records whose IP matches a forwarding rule, and CNAME
 * records that resolve to one of those A records.
 */
export function useUpstreamLoadBalancerChain(
  urlMapEntity: Entity,
): UpstreamLoadBalancerChain {
  const catalogApi = useApi(catalogApiRef);
  const [chain, setChain] = useState<UpstreamLoadBalancerChain>(EMPTY);

  useEffect(() => {
    let cancelled = false;
    setChain(EMPTY);

    // This hook is mounted on every gcp-lb entity page (UrlMap, BackendService,
    // ForwardingRule, TargetProxy all share that service annotation) — skip the
    // fetch chain entirely unless this actually is a UrlMap.
    if (!isAssetType(urlMapEntity, 'compute.googleapis.com/UrlMap')) {
      setChain({ ...EMPTY, loading: false });
      return undefined;
    }

    async function run() {
      try {
        const proxyLevel = await fetchDependents(catalogApi, [urlMapEntity]);
        const proxies = proxyLevel.entities.filter(e =>
          isAssetType(
            e,
            'compute.googleapis.com/TargetHttpProxy',
            'compute.googleapis.com/TargetHttpsProxy',
          ),
        );
        const proxyRefs = new Set(proxies.map(stringifyEntityRef));
        const proxyEdges = proxyLevel.edges.filter(ed =>
          proxyRefs.has(ed.from),
        );

        const frLevel = await fetchDependents(catalogApi, proxies);
        const forwardingRules = frLevel.entities.filter(e =>
          isAssetType(e, 'compute.googleapis.com/ForwardingRule'),
        );
        const frRefs = new Set(forwardingRules.map(stringifyEntityRef));
        const frEdges = frLevel.edges.filter(ed => frRefs.has(ed.from));

        const dnsLevel = await fetchDependents(catalogApi, forwardingRules);
        const dnsRecords = dnsLevel.entities.filter(e =>
          isDnsRecordType(e, 'A', 'AAAA'),
        );
        const dnsRefs = new Set(dnsRecords.map(stringifyEntityRef));
        const dnsEdges = dnsLevel.edges.filter(ed => dnsRefs.has(ed.from));

        const cnameLevel = await fetchDependents(catalogApi, dnsRecords);
        const cnames = cnameLevel.entities.filter(e =>
          isDnsRecordType(e, 'CNAME'),
        );
        const cnameRefs = new Set(cnames.map(stringifyEntityRef));
        const cnameEdges = cnameLevel.edges.filter(ed =>
          cnameRefs.has(ed.from),
        );

        if (!cancelled) {
          setChain({
            proxies,
            forwardingRules,
            dnsRecords,
            cnames,
            edges: [...proxyEdges, ...frEdges, ...dnsEdges, ...cnameEdges],
            loading: false,
          });
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setChain({
            ...EMPTY,
            loading: false,
            error: e instanceof Error ? e.message : String(e),
          });
        }
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [catalogApi, urlMapEntity]);

  return chain;
}
